import graphqlClient from './graphqlClient';

import { environment } from '../environment/environment';

/**
 * Generic CRUD against the 8 `mappings.*` tables backing the admin-only Data Mappings
 * area. All tables use a composite `varchar` primary key except `revenue_category` and
 * `wheel`, which keep `integer`. Hasura access is restricted server-side to the
 * `administrator` role only.
 */

export interface MappingPageOptions {
  limit: number;
  offset: number;
  sortField: string;
  sortDir: 'asc' | 'desc';
  search?: string;
  searchColumns?: string[];
  equalsFilter?: Record<string, string | undefined>;
}

export interface MappingPageResult<T> {
  rows: T[];
  totalCount: number;
}

/** Escapes Postgres LIKE/ILIKE metacharacters (`%`, `_`, `\`) so a value is matched literally
 * rather than as a wildcard pattern. */
const escapeLike = (val: string): string => val.replace(/[\\%_]/g, (m) => `\\${m}`);

/** Builds a Hasura `_bool_exp` object (passed as a plain GraphQL variable — no string templating
 * needed) from a search term (OR'd `_ilike` across `searchColumns`) plus optional equality filters.
 * Both use `_ilike` rather than `_eq`/plain equality so filtering is case-insensitive — dropdown
 * options (e.g. "Diy") don't always match the casing stored in the DB. */
const buildWhere = (opts: MappingPageOptions): Record<string, unknown> | undefined => {
  const andParts: Record<string, unknown>[] = [];

  if (opts.search && opts.searchColumns && opts.searchColumns.length > 0) {
    const term = `%${opts.search}%`;
    andParts.push({ _or: opts.searchColumns.map((col) => ({ [col]: { _ilike: term } })) });
  }

  if (opts.equalsFilter) {
    Object.entries(opts.equalsFilter).forEach(([col, val]) => {
      if (val === undefined || val === null || val === '') return;
      andParts.push({ [col]: { _ilike: escapeLike(val) } });
    });
  }

  if (andParts.length === 0) return undefined;
  return { _and: andParts };
};

/** Fetches one page of a mapping table: server-side search (`_ilike` OR across `searchColumns`),
 * a single-field sort, an optional equality filter (e.g. channel/customer_type dropdown), and the
 * matching total count in the same round trip. */
export const fetchMappingPage = async <T>(
  table: string,
  columns: string[],
  opts: MappingPageOptions
): Promise<MappingPageResult<T>> => {
  const where = buildWhere(opts);

  const query = `
    query FetchMappingPage($where: mappings_${table}_bool_exp, $limit: Int!, $offset: Int!, $orderBy: [mappings_${table}_order_by!]!) {
      rows: mappings_${table}(where: $where, limit: $limit, offset: $offset, order_by: $orderBy) {
        ${columns.join('\n        ')}
      }
      agg: mappings_${table}_aggregate(where: $where) {
        aggregate { count }
      }
    }
  `;

  const resp = await graphqlClient.request<{ rows: T[]; agg: { aggregate: { count: number } } }>(query, {
    where: where || {},
    limit: opts.limit,
    offset: opts.offset,
    orderBy: [{ [opts.sortField]: opts.sortDir }],
  });

  return { rows: resp.rows || [], totalCount: resp.agg?.aggregate?.count || 0 };
};

export const insertMappingRow = async <T>(table: string, columns: string[], object: Record<string, unknown>): Promise<T> => {
  const mutation = `
    mutation InsertMappingRow($object: mappings_${table}_insert_input!) {
      row: insert_mappings_${table}_one(object: $object) {
        ${columns.join('\n        ')}
      }
    }
  `;
  const resp = await graphqlClient.request<{ row: T }>(mutation, { object });
  return resp.row;
};

/** Bulk-inserts rows parsed from an uploaded CSV/Excel file, chunked to stay well under Hasura's
 * request-size limit (mirrors the 200-row batching in `storeIdService.ts`). Duplicates are
 * accepted as-is for now — no `on_conflict`/dedup, since there's no unique-key strategy yet. */
export const insertMappingRows = async <T>(
  table: string,
  columns: string[],
  objects: Record<string, unknown>[]
): Promise<T[]> => {
  const BATCH_SIZE = 200;
  const mutation = `
    mutation InsertMappingRows($objects: [mappings_${table}_insert_input!]!) {
      insert: insert_mappings_${table}(objects: $objects) {
        returning { ${columns.join('\n        ')} }
      }
    }
  `;

  const results: T[] = [];
  for (let i = 0; i < objects.length; i += BATCH_SIZE) {
    const batch = objects.slice(i, i + BATCH_SIZE);
    const resp = await graphqlClient.request<{ insert: { returning: T[] } }>(mutation, { objects: batch });
    results.push(...resp.insert.returning);
  }
  return results;
};

const gqlIdType = (table: string): string => {
  if (table === 'revenue_category' || table === 'wheel') return 'Int';
  return 'String';
};

export const updateMappingRow = async (table: string, id: string | number, patch: Record<string, unknown>): Promise<void> => {
  const idType = gqlIdType(table);
  const mutation = `
    mutation UpdateMappingRow($id: ${idType}!, $set: mappings_${table}_set_input!) {
      update_mappings_${table}_by_pk(pk_columns: { id: $id }, _set: $set) { id }
    }
  `;
  await graphqlClient.request(mutation, { id, set: patch });
};

export const deleteMappingRow = async (table: string, id: string | number): Promise<void> => {
  const idType = gqlIdType(table);
  const mutation = `
    mutation DeleteMappingRow($id: ${idType}!) {
      delete_mappings_${table}_by_pk(id: $id) { id }
    }
  `;
  await graphqlClient.request(mutation, { id });
};

export interface UploadResult {
  valid: boolean;
  summary: {
    totalRows: number;
    toUpdate: number;
    toInsert: number;
    skipped: number;
    errorCount: number;
  };
  warnings: Array<{ type: string; message?: string; columns?: string[]; count?: number; sample?: string[] }>;
  errors: Array<{ type: string; message: string; row?: number; field?: string }>;
}

export async function uploadMappingFile(
  file: File,
  mappingType: string,
  dryRun: boolean,
): Promise<UploadResult> {
  const gqlUrl = new URL(environment.graphqlEndpoint);
  let uploadUrl: string;
  if (gqlUrl.hostname === 'localhost' || gqlUrl.hostname === '127.0.0.1') {
    uploadUrl = `${gqlUrl.protocol}//localhost:8000/api/atlas/data-mappings/upload`;
  } else {
    uploadUrl = `${gqlUrl.origin}/api/api/atlas/data-mappings/upload`;
  }

  const token = localStorage.getItem('access_token');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('mappingType', mappingType);
  formData.append('dryRun', String(dryRun));

  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody?.details || errorBody?.error || `Upload failed: ${response.statusText}`);
  }

  return response.json();
}
